// ═══════════════════════════════════════════════════════════
// Shelter Store — Shelters, Survivors, Families, Pets, Vehicles,
// Medical Log, Transfers, Referrals
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';

// KV helpers for tables not yet in V2 Ignatius
const WORKER = import.meta.env.DEV
  ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
  : '/api/worker';
const KV_H = { 'Content-Type': 'application/json', Origin: import.meta.env.VITE_ORIGIN || 'https://apps.opengov.com' };
async function kvGet<T>(key: string): Promise<T[]> {
  try {
    const r = await fetch(`${WORKER}/api/config/${key}`, { headers: KV_H });
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d) ? d : (d?.value ? JSON.parse(d.value) : []);
  } catch { return []; }
}
async function kvPut(key: string, value: unknown): Promise<void> {
  await fetch(`${WORKER}/api/config/${key}`, { method: 'PUT', headers: KV_H, body: JSON.stringify(value) });
}
import type { Shelter, Survivor, ShelterFamily, ShelterPet, ShelterVehicle, MedicalLogEntry, ShelterTransfer, ShelterReferral } from '../types/shelter';
// Alias — pages use ShelterMedicalEntry; our types file exports MedicalLogEntry
type ShelterMedicalEntry = MedicalLogEntry;
import type { QueryOptions } from '../api/types';

const PAGE_SIZE = 100;

function getIncidentFilter(): { field: string; operator: 'eq'; value: unknown }[] {
  const inc = useAuthStore.getState().activeIncident;
  return inc ? [{ field: FIELDS.shelters.incidentId, operator: 'eq', value: inc.id }] : [];
}

/** Build a filter for a field that shares incidentId (survivors table) */
function getSurvivorIncidentFilter(): { field: string; operator: 'eq'; value: unknown }[] {
  const inc = useAuthStore.getState().activeIncident;
  return inc ? [{ field: FIELDS.survivors.incidentId, operator: 'eq', value: inc.id }] : [];
}

const FS = FIELDS.shelters;
const FSv = FIELDS.survivors;
const FFam = FIELDS.shelterFamilies;
const FPet = FIELDS.shelterPets;
const FVeh = FIELDS.shelterVehicles;
const FMed = FIELDS.shelterMedicalLog;
const FTr = FIELDS.shelterTransfers;
const FRef = FIELDS.shelterReferrals;
const FSI = FIELDS.shelterIncidents;
const FSup = FIELDS.shelterSupplies;
const FReu = FIELDS.shelterReunification;

function mapShelter(r: Record<string, unknown>): Shelter {
  const incidentId = String(r[FS.incidentId] || '');
  const lat = r[FS.latitude];
  const lng = r[FS.longitude];
  const latStr = lat != null ? String(lat) : '';
  const lngStr = lng != null ? String(lng) : '';
  const isADA = Boolean(r[FS.isADA]);
  const isPetFriendly = Boolean(r[FS.isPetFriendly]);
  const amenities = (r[FS.amenities] as string) || '';
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId,
    name: (r[FS.name] as string) || '', address: (r[FS.address] as string) || '',
    city: (r[FS.city] as string) || '', state: (r[FS.state] as string) || '', zip: (r[FS.zip] as string) || '',
    latitude: latStr, longitude: lngStr,
    capacity: Number(r[FS.capacity]) || 0, currentPopulation: Number(r[FS.currentPopulation]) || 0,
    shelterType: (r[FS.shelterType] as string) || 'general', status: (r[FS.status] as string) || 'standby',
    // V2 schema fields
    isADA, isPetFriendly, amenities,
    // Aliases used by Shelters.tsx and type definition
    isAda: isADA, petFriendly: isPetFriendly, facilities: amenities,
    contactName: (r[FS.contactName] as string) || '',
    contactPhone: (r[FS.contactPhone] as string) || '',
    contactEmail: '',    // not in V2 schema
    operatingOrg: (r[FS.operatingOrg] as string) || '',
    openedAt: '',        // not in V2 schema
    closedAt: '',        // not in V2 schema
    notes: (r[FS.notes] as string) || '',
  } as any;
}

function mapSurvivor(r: Record<string, unknown>): Survivor {
  const address = (r[FSv.address] as string) || '';
  const checkInTime = (r[FSv.checkInTime] as string) || '';
  const registeredBy = (r[FSv.registeredBy] as string) || '';
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    shelterId: String(r[FSv.shelterId] || ''),
    incidentId: String(r[FSv.incidentId] || ''),
    familyId: String(r[FSv.familyId] || ''),
    firstName: (r[FSv.firstName] as string) || '', lastName: (r[FSv.lastName] as string) || '',
    dateOfBirth: (r[FSv.dateOfBirth] as string) || '', gender: (r[FSv.gender] as string) || '',
    phone: (r[FSv.phone] as string) || '', email: (r[FSv.email] as string) || '',
    // V2 stores street in 'address'; map to both address and homeAddress for page compat
    address,
    homeAddress: address,
    city: (r[FSv.city] as string) || '',
    state: (r[FSv.state] as string) || '', zip: (r[FSv.zip] as string) || '',
    county: '',            // not in V2 schema — default to empty
    emergencyContactName: (r[FSv.emergencyContactName] as string) || '',
    emergencyContactPhone: (r[FSv.emergencyContactPhone] as string) || '',
    medicalNeeds: (r[FSv.medicalNeeds] as string) || '',
    dietaryNeeds: (r[FSv.dietaryNeeds] as string) || '',
    specialNeeds: (r[FSv.medicalNeeds] as string) || '', // alias for pages that use specialNeeds
    medications: '[]',      // not in V2 schema
    hasPets: Boolean(r[FSv.hasPets]), status: ((r[FSv.status] as string) || 'checked_in') as import('../types/shelter').SurvivorStatus,
    checkInTime,
    checkOutTime: (r[FSv.checkOutTime] as string) || '',
    registeredBy,
    // Fields not in V2 schema — provide safe defaults
    cotAssignment: '',
    qrCode: '',
    isMinor: false,
    isUnaccompaniedMinor: false,
    guardianId: '',
    photoUrl: '',
    intakeTimestamp: checkInTime,  // alias: pages use intakeTimestamp
    intakeBy: registeredBy,        // alias: pages use intakeBy
    dischargeTimestamp: '',
    dischargeReason: '',
    dischargedBy: '',
    notes: (r[FSv.notes] as string) || '',
  } as any as Survivor;
}

function mapFamily(r: Record<string, unknown>): ShelterFamily {
  const headOfHousehold = (r[FFam.headOfHousehold] as string) || '';
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    shelterId: Number(r[FFam.shelterId]) || 0,
    familyName: (r[FFam.familyName] as string) || '',
    headOfHousehold,
    headOfHouseholdId: headOfHousehold, // alias — pages use headOfHouseholdId
    memberCount: Number(r[FFam.memberCount]) || 0,
    memberIds: '[]',                    // not in V2 schema yet
    unit: (r[FFam.unit] as string) || '',
    notes: (r[FFam.notes] as string) || '',
  } as any;
}

function mapPet(r: Record<string, unknown>): ShelterPet {
  const petName = (r[FPet.petName] as string) || '';
  const ownerId = String(r[FPet.survivorId] || '');
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    survivorId: ownerId,
    ownerId,                        // alias — pages use ownerId
    shelterId: String(r[FPet.shelterId] || ''),
    petName,
    name: petName,                  // alias — pages use name
    species: (r[FPet.species] as string) || '',
    breed: (r[FPet.breed] as string) || '',
    color: (r[FPet.color] as string) || '',
    weight: (r[FPet.weight] as string) || '',
    vaccinated: Boolean(r[FPet.vaccinated]),
    specialNeeds: (r[FPet.specialNeeds] as string) || '',
    notes: (r[FPet.notes] as string) || '',
    // Fields not yet in V2 schema — provide defaults
    qrCode: '',
    status: 'sheltered' as any,
    medicalNeeds: '',
    locationInShelter: '',
    photoUrl: '',
  } as any;
}

function mapVehicle(r: Record<string, unknown>): ShelterVehicle {
  const ownerId = String(r[FVeh.survivorId] || '');
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    survivorId: ownerId,
    ownerId,                        // alias — pages use ownerId
    shelterId: String(r[FVeh.shelterId] || ''),
    make: (r[FVeh.make] as string) || '',
    model: (r[FVeh.model] as string) || '',
    year: (r[FVeh.year] as string) || '',
    color: (r[FVeh.color] as string) || '',
    licensePlate: (r[FVeh.licensePlate] as string) || '',
    parkingLocation: (r[FVeh.parkingLocation] as string) || '',
    notes: (r[FVeh.notes] as string) || '',
    state: (r[FVeh.state] as string) || '',
    status: ((r[FVeh.status] as string) || 'parked') as import('../types/shelter').VehicleStatus,
    vin: '',
    qrCode: '',
    photoUrl: '',
  } as any as ShelterVehicle;
}

function mapMedical(r: Record<string, unknown>): ShelterMedicalEntry {
  const incidentType = (r[FMed.incidentType] as string) || '';
  const treatedBy = (r[FMed.treatedBy] as string) || '';
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    survivorId: String(r[FMed.survivorId] || ''),
    shelterId: String(r[FMed.shelterId] || ''),
    incidentType,
    entryType: incidentType,        // alias — pages use entryType
    description: (r[FMed.description] as string) || '',
    treatment: (r[FMed.treatment] as string) || '',
    severity: (r[FMed.severity] as string) || '',
    treatedBy,
    administeredBy: treatedBy,      // alias — pages use administeredBy
    referral: (r[FMed.referral] as string) || '',
    timestamp: (r[FMed.timestamp] as string) || '',
    notes: (r[FMed.notes] as string) || '',
    medicationName: (r[FMed.medicationName] as string) || '',
    dosage: (r[FMed.dosage] as string) || '',
    followUpNeeded: Boolean(r[FMed.followUpNeeded]),
    followUpDate: (r[FMed.followUpDate] as string) || '',
  } as any;
}

function mapTransfer(r: Record<string, unknown>): ShelterTransfer {
  const transferDate = (r[FTr.transferDate] as string) || '';
  const authorizedBy = (r[FTr.authorizedBy] as string) || '';
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    survivorId: String(r[FTr.survivorId] || ''),
    fromShelterId: String(r[FTr.fromShelterId] || ''),
    toShelterId: String(r[FTr.toShelterId] || ''),
    reason: (r[FTr.reason] as string) || '',
    transferDate,
    initiatedAt: transferDate,      // alias — pages use initiatedAt
    authorizedBy,
    initiatedBy: authorizedBy,      // alias — pages use initiatedBy
    notes: (r[FTr.notes] as string) || '',
    status: ((r[FTr.status] as string) || 'pending') as any,
    survivorName: '',
    fromShelterName: '',
    toShelterName: '',
    completedAt: (r[FTr.completedAt] as string) || '',
  } as any;
}

function mapReferral(r: Record<string, unknown>): ShelterReferral {
  return {
    id: r.record_id as number, Rid: r.record_id as number,
    incidentId: '',
    survivorId: String(r[FRef.survivorId] || ''),
    survivorName: '',    // no dedicated field in rebuild app schema yet
    referralType: (r[FRef.referralType] as string) || '',
    agency: (r[FRef.agency] as string) || '',
    caseNumber: '',      // no dedicated field yet
    status: ((r[FRef.status] as string) || 'pending') as import('../types/shelter').ReferralStatus,
    dateMade: '',        // no dedicated field yet
    completedDate: '',   // no dedicated field yet
    madeBy: '',          // no dedicated field yet
    notes: (r[FRef.notes] as string) || '',
    // Legacy fields still in schema
    shelterId: String(r[FRef.shelterId] || ''),
    contactInfo: (r[FRef.contactInfo] as string) || '',
  } as any as ShelterReferral;
}

function petToPayload(rawData: Partial<ShelterPet>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  // Accept both survivorId and ownerId (pages send ownerId)
  const ownerVal = data.ownerId ?? (data as any).survivorId;
  if (ownerVal !== undefined) p[FPet.survivorId] = ownerVal;
  if (data.shelterId !== undefined) p[FPet.shelterId] = data.shelterId;
  // Accept both name and petName (pages send name)
  const nameVal = data.name ?? (data as any).petName;
  if (nameVal !== undefined) p[FPet.petName] = nameVal;
  if (data.species !== undefined) p[FPet.species] = data.species;
  if (data.breed !== undefined) p[FPet.breed] = data.breed;
  if (data.color !== undefined) p[FPet.color] = data.color;
  if (data.weight !== undefined) p[FPet.weight] = data.weight;
  if (data.vaccinated !== undefined) p[FPet.vaccinated] = data.vaccinated;
  if (data.specialNeeds !== undefined) p[FPet.specialNeeds] = data.specialNeeds;
  if (data.notes !== undefined) p[FPet.notes] = data.notes;
  return p;
}

function vehicleToPayload(rawData: Partial<ShelterVehicle>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  // Accept both ownerId (pages) and survivorId (legacy)
  const ownerVal = data.ownerId ?? data.survivorId;
  if (ownerVal !== undefined) p[FVeh.survivorId] = ownerVal;
  if (data.shelterId !== undefined) p[FVeh.shelterId] = data.shelterId;
  if (data.make !== undefined) p[FVeh.make] = data.make;
  if (data.model !== undefined) p[FVeh.model] = data.model;
  if (data.year !== undefined) p[FVeh.year] = data.year;
  if (data.color !== undefined) p[FVeh.color] = data.color;
  if (data.licensePlate !== undefined) p[FVeh.licensePlate] = data.licensePlate;
  if (data.parkingLocation !== undefined) p[FVeh.parkingLocation] = data.parkingLocation;
  if (data.notes !== undefined) p[FVeh.notes] = data.notes;
  if (data.state !== undefined) p[FVeh.state] = data.state;
  if (data.status !== undefined) p[FVeh.status] = data.status;
  return p;
}

function familyToPayload(rawData: Partial<ShelterFamily>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  if (data.shelterId !== undefined) p[FFam.shelterId] = data.shelterId;
  if (data.familyName !== undefined) p[FFam.familyName] = data.familyName;
  // Accept both headOfHouseholdId (pages) and headOfHousehold (legacy)
  const headVal = data.headOfHouseholdId ?? data.headOfHousehold;
  if (headVal !== undefined) p[FFam.headOfHousehold] = headVal;
  if (data.memberCount !== undefined) p[FFam.memberCount] = data.memberCount;
  if (data.unit !== undefined) p[FFam.unit] = data.unit;
  if (data.notes !== undefined) p[FFam.notes] = data.notes;
  return p;
}

function shelterToPayload(rawData: Partial<Shelter>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  if (data.name !== undefined) p[FS.name] = data.name;
  if (data.address !== undefined) p[FS.address] = data.address;
  if (data.city !== undefined) p[FS.city] = data.city;
  if (data.state !== undefined) p[FS.state] = data.state;
  if (data.zip !== undefined) p[FS.zip] = data.zip;
  if (data.latitude !== undefined) p[FS.latitude] = data.latitude;
  if (data.longitude !== undefined) p[FS.longitude] = data.longitude;
  if (data.capacity !== undefined) p[FS.capacity] = data.capacity;
  if (data.currentPopulation !== undefined) p[FS.currentPopulation] = data.currentPopulation;
  if (data.shelterType !== undefined) p[FS.shelterType] = data.shelterType;
  if (data.status !== undefined) p[FS.status] = data.status;
  // Accept both isADA (GAB pages) and isAda (Mantine pages)
  const isADAVal = data.isADA ?? data.isAda;
  if (isADAVal !== undefined) p[FS.isADA] = isADAVal;
  // Accept both isPetFriendly (GAB pages) and petFriendly (Mantine pages)
  const petVal = data.isPetFriendly ?? data.petFriendly;
  if (petVal !== undefined) p[FS.isPetFriendly] = petVal;
  // Accept both amenities (GAB pages) and facilities (Mantine pages)
  const amenitiesVal = data.amenities ?? data.facilities;
  if (amenitiesVal !== undefined) p[FS.amenities] = amenitiesVal;
  if (data.contactName !== undefined) p[FS.contactName] = data.contactName;
  if (data.contactPhone !== undefined) p[FS.contactPhone] = data.contactPhone;
  if (data.operatingOrg !== undefined) p[FS.operatingOrg] = data.operatingOrg;
  if (data.incidentId !== undefined) p[FS.incidentId] = data.incidentId;
  if (data.notes !== undefined) p[FS.notes] = data.notes;
  return p;
}

function survivorToPayload(rawData: Partial<Survivor>): Record<string, unknown> {
  const data = rawData as any;
  const p: Record<string, unknown> = {};
  if (data.shelterId !== undefined) p[FSv.shelterId] = data.shelterId;
  if (data.incidentId !== undefined) p[FSv.incidentId] = data.incidentId;
  if (data.familyId !== undefined) p[FSv.familyId] = data.familyId;
  if (data.firstName !== undefined) p[FSv.firstName] = data.firstName;
  if (data.lastName !== undefined) p[FSv.lastName] = data.lastName;
  if (data.dateOfBirth !== undefined) p[FSv.dateOfBirth] = data.dateOfBirth;
  if (data.gender !== undefined) p[FSv.gender] = data.gender;
  if (data.phone !== undefined) p[FSv.phone] = data.phone;
  if (data.email !== undefined) p[FSv.email] = data.email;
  // Accept both address (V2 schema name) and homeAddress (type field name used by pages)
  const addressVal = data.address ?? data.homeAddress;
  if (addressVal !== undefined) p[FSv.address] = addressVal;
  if (data.city !== undefined) p[FSv.city] = data.city;
  if (data.state !== undefined) p[FSv.state] = data.state;
  if (data.zip !== undefined) p[FSv.zip] = data.zip;
  if (data.emergencyContactName !== undefined) p[FSv.emergencyContactName] = data.emergencyContactName;
  if (data.emergencyContactPhone !== undefined) p[FSv.emergencyContactPhone] = data.emergencyContactPhone;
  if (data.medicalNeeds !== undefined) p[FSv.medicalNeeds] = data.medicalNeeds;
  if (data.dietaryNeeds !== undefined) p[FSv.dietaryNeeds] = data.dietaryNeeds;
  if (data.hasPets !== undefined) p[FSv.hasPets] = data.hasPets;
  if (data.status !== undefined) p[FSv.status] = data.status;
  // Accept both intakeTimestamp (Intake.tsx) and checkInTime
  const checkInVal = data.intakeTimestamp ?? data.checkInTime;
  if (checkInVal !== undefined) p[FSv.checkInTime] = checkInVal;
  if (data.checkOutTime !== undefined) p[FSv.checkOutTime] = data.checkOutTime;
  // Accept both intakeBy (Intake.tsx) and registeredBy
  const registeredByVal = data.intakeBy ?? data.registeredBy;
  if (registeredByVal !== undefined) p[FSv.registeredBy] = registeredByVal;
  if (data.notes !== undefined) p[FSv.notes] = data.notes;
  return p;
}

interface ShelterState {
  // Shelters
  shelters: Shelter[];
  sheltersLoading: boolean;
  fetchShelters: () => Promise<void>;

  // Survivors (paginated)
  survivors: Survivor[];
  survivorsTotal: number;
  survivorsLoading: boolean;
  survivorsPage: number;
  fetchSurvivors: (opts?: Partial<QueryOptions>) => Promise<void>;
  setSurvivorsPage: (page: number) => void;

  // Related data (paginated for large tables)
  families: ShelterFamily[];
  familiesTotal: number;
  familiesPage: number;
  pets: ShelterPet[];
  petsTotal: number;
  petsPage: number;
  vehicles: ShelterVehicle[];
  vehiclesTotal: number;
  vehiclesPage: number;
  medicalLog: ShelterMedicalEntry[];
  medicalLogTotal: number;
  medicalLogPage: number;
  transfers: ShelterTransfer[];
  transfersTotal: number;
  transfersPage: number;
  referrals: ShelterReferral[];
  referralsTotal: number;
  referralsPage: number;
  fetchFamilies: (shelterId?: string | number) => Promise<void>;
  fetchPets: (shelterId?: string | number) => Promise<void>;
  fetchVehicles: (shelterId?: string | number) => Promise<void>;
  fetchMedicalLog: (shelterId?: string | number) => Promise<void>;
  fetchTransfers: (shelterId?: string | number) => Promise<void>;
  fetchReferrals: (shelterId?: string | number) => Promise<void>;
  setFamiliesPage: (page: number) => void;
  setPetsPage: (page: number) => void;
  setVehiclesPage: (page: number) => void;
  setMedicalLogPage: (page: number) => void;
  setTransfersPage: (page: number) => void;
  setReferralsPage: (page: number) => void;

  // CRUD — Shelters
  createShelter: (data: Record<string, unknown>) => Promise<number | null>;
  updateShelter: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteShelter: (id: number) => Promise<void>;

  // CRUD — Survivors
  createSurvivor: (data: Record<string, unknown>) => Promise<number | null>;
  updateSurvivor: (id: number, data: Partial<Survivor>) => Promise<void>;
  checkOutSurvivor: (id: number) => Promise<void>;

  // CRUD — Pets
  createPet: (data: Partial<ShelterPet>) => Promise<number | null>;
  updatePet: (id: number, data: Partial<ShelterPet>) => Promise<void>;
  deletePet: (id: number) => Promise<void>;

  // CRUD — Vehicles
  createVehicle: (data: Partial<ShelterVehicle>) => Promise<number | null>;
  updateVehicle: (id: number, data: Partial<ShelterVehicle>) => Promise<void>;
  deleteVehicle: (id: number) => Promise<void>;

  // CRUD — Families
  createFamily: (data: Partial<ShelterFamily>) => Promise<number | null>;
  updateFamily: (id: number, data: Partial<ShelterFamily>) => Promise<void>;
  deleteFamily: (id: number) => Promise<void>;

  // CRUD — Other
  createMedicalEntry: (data: Partial<ShelterMedicalEntry>) => Promise<number | null>;
  updateMedicalEntry: (id: number, data: Partial<ShelterMedicalEntry>) => Promise<void>;
  createTransfer: (data: Partial<ShelterTransfer>) => Promise<number | null>;
  updateTransfer: (id: number, data: Partial<ShelterTransfer>) => Promise<void>;
  createReferral: (data: Partial<ShelterReferral>) => Promise<number | null>;
  updateReferral: (id: number, data: Partial<ShelterReferral>) => Promise<void>;

  // Shelter incidents
  shelterIncidents: any[];
  loadingIncidents: boolean;
  loadShelterIncidents: () => Promise<void>;
  createShelterIncident: (data: any) => Promise<number | null>;
  updateShelterIncident: (id: number, data: any) => Promise<void>;

  // Supplies
  supplies: any[];
  loadingSupplies: boolean;
  loadSupplies: () => Promise<void>;
  createSupplyItem: (data: any) => Promise<number | null>;
  updateSupplyItem: (id: number, data: any) => Promise<void>;
  deleteSupplyItem: (id: number) => Promise<void>;

  // Reunification
  reunification: any[];
  loadingReunification: boolean;
  loadReunification: () => Promise<void>;
  createReunification: (data: any) => Promise<number | null>;
  updateReunification: (id: number, data: any) => Promise<void>;

  // Triage
  triageQuestions: any[];
  loadTriageQuestions: () => Promise<void>;
  triageAnswers: any[];
  loadTriageAnswers: (survivorId: string | number) => Promise<void>;
  loadingTriageQ: boolean;
  createTriageQuestion: (data: any) => Promise<number | null>;
  updateTriageQuestion: (id: any, data: any) => Promise<void>;
  deleteTriageQuestion: (id: any) => Promise<void>;
  createTriageAnswer: (data: any) => Promise<number | null>;

  // Property
  property: any[];
  loadProperty: () => Promise<void>;
  loadingProperty: boolean;
  createProperty: (data: any) => Promise<number | null>;
  updateProperty: (id: any, data: any) => Promise<void>;
  deleteProperty: (id: any) => Promise<void>;

  // V1 compat aliases
  loadShelters: () => Promise<void>;
  loadSurvivors: () => Promise<void>;
  loadFamilies: () => Promise<void>;
  loadPets: () => Promise<void>;
  loadVehicles: () => Promise<void>;
  loadMedicalLog: () => Promise<void>;
  loadTransfers: () => Promise<void>;
  loadReferrals: () => Promise<void>;
  loadingShelters: boolean;
  loadingSurvivors: boolean;
  loadingFamilies: boolean;
  loadingPets: boolean;
  loadingVehicles: boolean;
  loadingTransfers: boolean;
  loadingReferrals: boolean;
  loadingMedical: boolean;
  isLoading: boolean;
}

export const useShelterStore = create<ShelterState>((set, get) => ({
  shelters: [], sheltersLoading: false,
  survivors: [], survivorsTotal: 0, survivorsLoading: false, survivorsPage: 1,
  families: [], familiesTotal: 0, familiesPage: 1,
  pets: [], petsTotal: 0, petsPage: 1,
  vehicles: [], vehiclesTotal: 0, vehiclesPage: 1,
  medicalLog: [], medicalLogTotal: 0, medicalLogPage: 1,
  transfers: [], transfersTotal: 0, transfersPage: 1,
  referrals: [], referralsTotal: 0, referralsPage: 1,

  fetchShelters: async () => {
    set({ sheltersLoading: true });
    try {
      const incFilter = getIncidentFilter();
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelters, {
        filters: incFilter,
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ shelters: res.records.map(mapShelter) });
    } catch (e) { console.error('[Shelter] fetchShelters', e); }
    set({ sheltersLoading: false });
  },

  fetchSurvivors: async (opts) => {
    set({ survivorsLoading: true });
    try {
      const { survivorsPage } = get();
      const incFilter = getSurvivorIncidentFilter();
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.survivors, {
        limit: PAGE_SIZE, offset: (survivorsPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
        ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ survivors: res.records.map(mapSurvivor), survivorsTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchSurvivors', e); }
    set({ survivorsLoading: false });
  },

  setSurvivorsPage: (page) => { set({ survivorsPage: page }); get().fetchSurvivors(); },

  fetchFamilies: async (shelterId) => {
    try {
      const { familiesPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FFam.shelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterFamilies, {
        filters, limit: PAGE_SIZE, offset: (familiesPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ families: res.records.map(mapFamily), familiesTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchFamilies', e); }
  },

  setFamiliesPage: (page) => { set({ familiesPage: page }); get().fetchFamilies(); },

  fetchPets: async (shelterId) => {
    try {
      const { petsPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FPet.shelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterPets, {
        filters, limit: PAGE_SIZE, offset: (petsPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ pets: res.records.map(mapPet), petsTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchPets', e); }
  },

  setPetsPage: (page) => { set({ petsPage: page }); get().fetchPets(); },

  fetchVehicles: async (shelterId) => {
    try {
      const { vehiclesPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FVeh.shelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterVehicles, {
        filters, limit: PAGE_SIZE, offset: (vehiclesPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ vehicles: res.records.map(mapVehicle), vehiclesTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchVehicles', e); }
  },

  setVehiclesPage: (page) => { set({ vehiclesPage: page }); get().fetchVehicles(); },

  fetchMedicalLog: async (shelterId) => {
    try {
      const { medicalLogPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FMed.shelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterMedicalLog, {
        filters, limit: PAGE_SIZE, offset: (medicalLogPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ medicalLog: res.records.map(mapMedical), medicalLogTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchMedicalLog', e); }
  },

  setMedicalLogPage: (page) => { set({ medicalLogPage: page }); get().fetchMedicalLog(); },

  fetchTransfers: async (shelterId) => {
    try {
      const { transfersPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FTr.fromShelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterTransfers, {
        filters, limit: PAGE_SIZE, offset: (transfersPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ transfers: res.records.map(mapTransfer), transfersTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchTransfers', e); }
  },

  setTransfersPage: (page) => { set({ transfersPage: page }); get().fetchTransfers(); },

  fetchReferrals: async (shelterId) => {
    try {
      const { referralsPage } = get();
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (shelterId != null) filters.push({ field: FRef.shelterId, operator: 'eq', value: shelterId });
      const res = await queryRecords<Record<string, unknown>>(TABLES.shelterReferrals, {
        filters, limit: PAGE_SIZE, offset: (referralsPage - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ referrals: res.records.map(mapReferral), referralsTotal: res.total });
    } catch (e) { console.error('[Shelter] fetchReferrals', e); }
  },

  setReferralsPage: (page) => { set({ referralsPage: page }); get().fetchReferrals(); },

  createShelter: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelters, shelterToPayload(data as Partial<Shelter>));
      await get().fetchShelters();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createShelter', e); return null; }
  },
  updateShelter: async (id, data) => { try { await updateRecord(TABLES.shelters, id, shelterToPayload(data as Partial<Shelter>)); await get().fetchShelters(); } catch (e) { console.error('[Shelter]', e); } },
  deleteShelter: async (id) => { try { await deleteRecord(TABLES.shelters, id); await get().fetchShelters(); } catch (e) { console.error('[Shelter]', e); } },

  createSurvivor: async (data) => {
    try {
      const payload = survivorToPayload(data as Partial<Survivor>);
      payload[FSv.checkInTime] = new Date().toISOString();
      if (!payload[FSv.status]) payload[FSv.status] = 'checked_in';
      const res = await createRecord<Record<string, unknown>>(TABLES.survivors, payload);
      await get().fetchSurvivors();
      await get().fetchShelters();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createSurvivor', e); return null; }
  },

  updateSurvivor: async (id, data) => {
    try {
      await updateRecord(TABLES.survivors, id, survivorToPayload(data));
      await get().fetchSurvivors();
    } catch (e) { console.error('[Shelter] updateSurvivor', e); }
  },

  checkOutSurvivor: async (id) => {
    try {
      await updateRecord(TABLES.survivors, id, { [FSv.status]: 'checked_out', [FSv.checkOutTime]: new Date().toISOString() });
      await get().fetchSurvivors();
      await get().fetchShelters();
    } catch (e) { console.error('[Shelter] checkOut', e); }
  },

  // Pet CRUD
  createPet: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterPets, petToPayload(data));
      await get().fetchPets();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createPet', e); return null; }
  },
  updatePet: async (id, data) => {
    try { await updateRecord(TABLES.shelterPets, id, petToPayload(data)); await get().fetchPets(); }
    catch (e) { console.error('[Shelter] updatePet', e); }
  },
  deletePet: async (id) => {
    try { await deleteRecord(TABLES.shelterPets, id); await get().fetchPets(); }
    catch (e) { console.error('[Shelter] deletePet', e); }
  },

  // Vehicle CRUD
  createVehicle: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterVehicles, vehicleToPayload(data));
      await get().fetchVehicles();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createVehicle', e); return null; }
  },
  updateVehicle: async (id, data) => {
    try { await updateRecord(TABLES.shelterVehicles, id, vehicleToPayload(data)); await get().fetchVehicles(); }
    catch (e) { console.error('[Shelter] updateVehicle', e); }
  },
  deleteVehicle: async (id) => {
    try { await deleteRecord(TABLES.shelterVehicles, id); await get().fetchVehicles(); }
    catch (e) { console.error('[Shelter] deleteVehicle', e); }
  },

  // Family CRUD
  createFamily: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterFamilies, familyToPayload(data));
      await get().fetchFamilies();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createFamily', e); return null; }
  },
  updateFamily: async (id, data) => {
    try { await updateRecord(TABLES.shelterFamilies, id, familyToPayload(data)); await get().fetchFamilies(); }
    catch (e) { console.error('[Shelter] updateFamily', e); }
  },
  deleteFamily: async (id) => {
    try { await deleteRecord(TABLES.shelterFamilies, id); await get().fetchFamilies(); }
    catch (e) { console.error('[Shelter] deleteFamily', e); }
  },

  createMedicalEntry: async (data) => {
    try {
      const d = data as any;
      const p: Record<string, unknown> = {};
      if (d.survivorId) p[FMed.survivorId] = d.survivorId;
      if (d.shelterId) p[FMed.shelterId] = d.shelterId;
      // Accept both entryType (pages) and incidentType (legacy)
      const entryType = d.entryType ?? d.incidentType;
      if (entryType) p[FMed.incidentType] = entryType;
      if (d.description) p[FMed.description] = d.description;
      if (d.treatment) p[FMed.treatment] = d.treatment;
      if (d.severity) p[FMed.severity] = d.severity;
      // Accept both administeredBy (pages) and treatedBy (legacy)
      const administeredBy = d.administeredBy ?? d.treatedBy;
      if (administeredBy) p[FMed.treatedBy] = administeredBy;
      if (d.referral) p[FMed.referral] = d.referral;
      p[FMed.timestamp] = d.timestamp || new Date().toISOString();
      if (d.notes) p[FMed.notes] = d.notes;
      if (d.medicationName) p[FMed.medicationName] = d.medicationName;
      if (d.dosage) p[FMed.dosage] = d.dosage;
      if (d.followUpNeeded !== undefined) p[FMed.followUpNeeded] = d.followUpNeeded;
      if (d.followUpDate) p[FMed.followUpDate] = d.followUpDate;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterMedicalLog, p);
      await get().fetchMedicalLog();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createMedicalEntry', e); return null; }
  },

  createTransfer: async (data) => {
    try {
      const d = data as any;
      const p: Record<string, unknown> = {};
      if (d.survivorId) p[FTr.survivorId] = d.survivorId;
      if (d.fromShelterId) p[FTr.fromShelterId] = d.fromShelterId;
      if (d.toShelterId) p[FTr.toShelterId] = d.toShelterId;
      if (d.reason) p[FTr.reason] = d.reason;
      // Accept both initiatedAt (pages) and transferDate (legacy)
      const initiatedAt = d.initiatedAt ?? d.transferDate;
      p[FTr.transferDate] = initiatedAt || new Date().toISOString();
      // Accept both initiatedBy (pages) and authorizedBy (legacy)
      const initiatedBy = d.initiatedBy ?? d.authorizedBy;
      if (initiatedBy) p[FTr.authorizedBy] = initiatedBy;
      if (d.notes) p[FTr.notes] = d.notes;
      if (d.status) p[FTr.status] = d.status;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterTransfers, p);
      await get().fetchTransfers();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createTransfer', e); return null; }
  },

  createReferral: async (data) => {
    try {
      const d = data as any;
      const p: Record<string, unknown> = {};
      if (d.survivorId) p[FRef.survivorId] = d.survivorId;
      if (d.shelterId) p[FRef.shelterId] = d.shelterId;
      if (d.referralType) p[FRef.referralType] = d.referralType;
      if (d.agency) p[FRef.agency] = d.agency;
      if (d.contactInfo) p[FRef.contactInfo] = d.contactInfo;
      if (d.status) p[FRef.status] = d.status;
      if (d.notes) p[FRef.notes] = d.notes;
      const res = await createRecord<Record<string, unknown>>(TABLES.shelterReferrals, p);
      await get().fetchReferrals();
      return (res as any).record_id;
    } catch (e) { console.error('[Shelter] createReferral', e); return null; }
  },

  // V1 compat aliases
  loadShelters: async () => get().fetchShelters(),
  loadSurvivors: async () => get().fetchSurvivors(),
  loadFamilies: async () => get().fetchFamilies(),
  loadPets: async () => get().fetchPets(),
  loadVehicles: async () => get().fetchVehicles(),
  loadMedicalLog: async () => get().fetchMedicalLog(),
  loadTransfers: async () => get().fetchTransfers(),
  loadReferrals: async () => get().fetchReferrals(),
  loadingShelters: false,
  loadingSurvivors: false,
  loadingFamilies: false,
  isLoading: false,
  shelterIncidents: [] as any[],
  loadingIncidents: false,
  loadShelterIncidents: async () => {
    set({ loadingIncidents: true });
    try {
      // Filter by shelterId list from loaded shelters when available; otherwise fetch with limit/sort
      const shelterIds = get().shelters.map(s => s.id);
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      // shelterIncidents has shelterId FK — use incident-scoped shelters if we have them
      const incFilter = getIncidentFilter();
      // We can't filter shelterIncidents directly by incidentId (not on the table),
      // but we can scope to the first 200 ordered by most recent
      const r = await queryRecords<any>(TABLES.shelterIncidents, {
        filters,
        limit: PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      void shelterIds; // used for future shelterId-in filter
      void incFilter;
      set({ shelterIncidents: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id,
        incidentId: '',
        shelterId: String(row[FSI.shelterId] || ''),
        reportType: row[FSI.reportType] || 'other',
        severity: row[FSI.severity] || 'low',
        description: row[FSI.description] || '',
        involvedPartyIds: '[]',
        respondingStaff: '',
        resolution: '',
        reportedBy: row[FSI.reportedBy] || '',
        reportedAt: row[FSI.reportedAt] || '',
        resolvedAt: row[FSI.resolvedAt] || '',
        notes: row[FSI.notes] || '',
      })) });
    } catch (e) { console.error('[Shelter]', e); }
    set({ loadingIncidents: false });
  },
  createShelterIncident: async (data: any) => {
    try {
      const p: any = {};
      if (data.shelterId) p[FSI.shelterId] = data.shelterId;
      if (data.reportType || data.incidentType) p[FSI.reportType] = data.reportType || data.incidentType;
      if (data.severity) p[FSI.severity] = data.severity;
      if (data.description) p[FSI.description] = data.description;
      if (data.reportedBy) p[FSI.reportedBy] = data.reportedBy;
      if (data.reportedAt) p[FSI.reportedAt] = data.reportedAt;
      if (data.notes) p[FSI.notes] = data.notes;
      const r = await createRecord<any>(TABLES.shelterIncidents, p);
      await get().loadShelterIncidents();
      return r.record_id as number;
    } catch (e) { console.error('[Shelter]', e); return null; }
  },
  updateShelterIncident: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.shelterId !== undefined) p[FSI.shelterId] = data.shelterId;
      if (data.reportType !== undefined || data.incidentType !== undefined) p[FSI.reportType] = data.reportType ?? data.incidentType;
      if (data.severity !== undefined) p[FSI.severity] = data.severity;
      if (data.description !== undefined) p[FSI.description] = data.description;
      if (data.reportedBy !== undefined) p[FSI.reportedBy] = data.reportedBy;
      if (data.reportedAt !== undefined) p[FSI.reportedAt] = data.reportedAt;
      if (data.resolvedAt !== undefined) p[FSI.resolvedAt] = data.resolvedAt;
      if (data.notes !== undefined) p[FSI.notes] = data.notes;
      await updateRecord(TABLES.shelterIncidents, id, p);
      await get().loadShelterIncidents();
    } catch (e) { console.error('[Shelter]', e); }
  },
  triageQuestions: [] as any[],
  loadTriageQuestions: async () => {
    const qs = await kvGet<any>('shelter-triage-questions');
    set({ triageQuestions: qs });
  },
  loadingTriageQ: false,
  createTriageQuestion: async (data: any) => {
    const qs = await kvGet<any>('shelter-triage-questions');
    const newQ = { ...data, id: Date.now() };
    const updated = [...qs, newQ];
    await kvPut('shelter-triage-questions', updated);
    set({ triageQuestions: updated });
    return newQ.id;
  },
  updateTriageQuestion: async (id: any, data: any) => {
    const qs = await kvGet<any>('shelter-triage-questions');
    const updated = qs.map((q: any) => q.id === id || String(q.id) === String(id) ? { ...q, ...data } : q);
    await kvPut('shelter-triage-questions', updated);
    set({ triageQuestions: updated });
  },
  deleteTriageQuestion: async (id: any) => {
    const qs = await kvGet<any>('shelter-triage-questions');
    const updated = qs.filter((q: any) => q.id !== id && String(q.id) !== String(id));
    await kvPut('shelter-triage-questions', updated);
    set({ triageQuestions: updated });
  },
  triageAnswers: [] as any[],
  loadTriageAnswers: async (survivorId: any) => {
    const all = await kvGet<any>('shelter-triage-answers');
    set({ triageAnswers: all.filter((a: any) => String(a.survivorId) === String(survivorId)) });
  },
  createTriageAnswer: async (data: any) => {
    const all = await kvGet<any>('shelter-triage-answers');
    const newA = { ...data, id: Date.now() };
    await kvPut('shelter-triage-answers', [...all, newA]);
    return newA.id;
  },
  supplies: [] as any[],
  loadingSupplies: false,
  loadSupplies: async () => {
    set({ loadingSupplies: true });
    try {
      // shelterSupplies has shelterId FK — no direct incidentId; fetch with reasonable limit
      const r = await queryRecords<any>(TABLES.shelterSupplies, {
        filters: [],
        limit: PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ supplies: r.records.map((row: any) => ({
        id: row.record_id, Rid: row.record_id, shelterId: String(row[FSup.shelterId] || ''), incidentId: '',
        itemName: row[FSup.itemName] || '', category: row[FSup.category] || '',
        quantity: Number(row[FSup.quantity]) || 0, unit: row[FSup.unit] || '',
        reorderLevel: Number(row[FSup.reorderLevel]) || 0,
        reorderThreshold: Number(row[FSup.reorderLevel]) || 0,  // alias — pages use reorderThreshold
        lastRestocked: row[FSup.lastRestocked] || '', notes: row[FSup.notes] || '',
      })) });
    } catch (e) { console.error('[Shelter]', e); }
    set({ loadingSupplies: false });
  },
  createSupplyItem: async (data: any) => {
    try {
      const p: any = {};
      if (data.shelterId) p[FSup.shelterId] = data.shelterId;
      if (data.itemName) p[FSup.itemName] = data.itemName;
      if (data.category) p[FSup.category] = data.category;
      if (data.quantity !== undefined) p[FSup.quantity] = data.quantity;
      if (data.unit) p[FSup.unit] = data.unit;
      // Accept both reorderThreshold (pages) and reorderLevel (legacy)
      const reorder = data.reorderThreshold ?? data.reorderLevel;
      if (reorder !== undefined) p[FSup.reorderLevel] = reorder;
      if (data.notes) p[FSup.notes] = data.notes;
      const r = await createRecord<any>(TABLES.shelterSupplies, p);
      await get().loadSupplies();
      return r.record_id as number;
    } catch (e) { console.error('[Shelter]', e); return null; }
  },
  updateSupplyItem: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.shelterId !== undefined) p[FSup.shelterId] = data.shelterId;
      if (data.quantity !== undefined) p[FSup.quantity] = data.quantity;
      if (data.itemName !== undefined) p[FSup.itemName] = data.itemName;
      if (data.category !== undefined) p[FSup.category] = data.category;
      if (data.unit !== undefined) p[FSup.unit] = data.unit;
      if (data.lastRestocked !== undefined) p[FSup.lastRestocked] = data.lastRestocked;
      // Accept both reorderThreshold (pages) and reorderLevel (legacy)
      const reorder = data.reorderThreshold ?? data.reorderLevel;
      if (reorder !== undefined) p[FSup.reorderLevel] = reorder;
      if (data.notes !== undefined) p[FSup.notes] = data.notes;
      await updateRecord(TABLES.shelterSupplies, id, p);
      await get().loadSupplies();
    } catch (e) { console.error('[Shelter]', e); }
  },
  deleteSupplyItem: async (id: any) => {
    try { await deleteRecord(TABLES.shelterSupplies, id); await get().loadSupplies(); }
    catch (e) { console.error('[Shelter]', e); }
  },
  property: [] as any[],
  loadProperty: async () => {
    const items = await kvGet<any>('shelter-property-items');
    set({ property: items });
  },
  loadingProperty: false,
  createProperty: async (data: any) => {
    const items = await kvGet<any>('shelter-property-items');
    const newItem = { ...data, id: Date.now() };
    await kvPut('shelter-property-items', [...items, newItem]);
    set({ property: [...items, newItem] });
    return newItem.id;
  },
  updateProperty: async (id: any, data: any) => {
    const items = await kvGet<any>('shelter-property-items');
    const updated = items.map((item: any) => item.id === id || String(item.id) === String(id) ? { ...item, ...data } : item);
    await kvPut('shelter-property-items', updated);
    set({ property: updated });
  },
  deleteProperty: async (id: any) => {
    const items = await kvGet<any>('shelter-property-items');
    const updated = items.filter((item: any) => item.id !== id && String(item.id) !== String(id));
    await kvPut('shelter-property-items', updated);
    set({ property: updated });
  },
  reunification: [] as any[],
  loadingReunification: false,
  loadReunification: async () => {
    set({ loadingReunification: true });
    try {
      // shelterReunification has shelterId FK — filter by shelterId list from loaded shelters
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      const r = await queryRecords<any>(TABLES.shelterReunification, {
        filters,
        limit: PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ reunification: r.records.map((row: any) => {
        const requestorName = row[FReu.requestorName] || '';
        const requestorPhone = row[FReu.requestorPhone] || '';
        const requestorEmail = row[FReu.requestorEmail] || '';
        const requestorRelationship = row[FReu.requestorRelationship] || '';
        const missingPersonDOB = row[FReu.missingPersonDOB] || '';
        return {
          id: row.record_id, Rid: row.record_id, shelterId: row[FReu.shelterId],
          requestorName, requestedByName: requestorName,
          requestorPhone, requestedByPhone: requestorPhone,
          requestorEmail, requestedByEmail: requestorEmail,
          requestorRelationship, relationship: requestorRelationship,
          missingPersonName: row[FReu.missingPersonName] || '',
          missingPersonDOB, missingPersonDob: missingPersonDOB,
          missingPersonPhone: row[FReu.missingPersonPhone] || '',
          missingPersonDescription: row[FReu.missingPersonDescription] || '',
          matchedSurvivorId: row[FReu.matchedSurvivorId] || null,
          matchConfidence: row[FReu.matchConfidence] || '',
          status: row[FReu.status] || 'searching',
          notes: row[FReu.notes] || '',
          // Defaults for fields not in DB
          incidentId: '', source: '', lastKnownLocation: '', lastKnownDate: '',
          missingPersonAge: '', photoUrl: '', createdAt: '', resolvedAt: '',
        };
      }) });
    } catch (e) { console.error('[Shelter]', e); }
    set({ loadingReunification: false });
  },
  createReunification: async (data: any) => {
    try {
      const p: any = {};
      if (data.shelterId) p[FReu.shelterId] = data.shelterId;
      const name = data.requestorName ?? data.requestedByName;
      if (name) p[FReu.requestorName] = name;
      const phone = data.requestorPhone ?? data.requestedByPhone;
      if (phone) p[FReu.requestorPhone] = phone;
      const email = data.requestorEmail ?? data.requestedByEmail;
      if (email) p[FReu.requestorEmail] = email;
      const rel = data.requestorRelationship ?? data.relationship;
      if (rel) p[FReu.requestorRelationship] = rel;
      if (data.missingPersonName) p[FReu.missingPersonName] = data.missingPersonName;
      const dob = data.missingPersonDOB ?? data.missingPersonDob;
      if (dob) p[FReu.missingPersonDOB] = dob;
      if (data.missingPersonPhone) p[FReu.missingPersonPhone] = data.missingPersonPhone;
      if (data.missingPersonDescription) p[FReu.missingPersonDescription] = data.missingPersonDescription;
      if (data.status) p[FReu.status] = data.status;
      if (data.notes) p[FReu.notes] = data.notes;
      const r = await createRecord<any>(TABLES.shelterReunification, p);
      await get().loadReunification();
      return r.record_id as number;
    } catch (e) { console.error('[Shelter]', e); return null; }
  },
  updateReunification: async (id: any, data: any) => {
    try {
      const p: any = {};
      const name = data.requestorName ?? data.requestedByName;
      if (name !== undefined) p[FReu.requestorName] = name;
      const phone = data.requestorPhone ?? data.requestedByPhone;
      if (phone !== undefined) p[FReu.requestorPhone] = phone;
      const email = data.requestorEmail ?? data.requestedByEmail;
      if (email !== undefined) p[FReu.requestorEmail] = email;
      const rel = data.requestorRelationship ?? data.relationship;
      if (rel !== undefined) p[FReu.requestorRelationship] = rel;
      if (data.missingPersonName !== undefined) p[FReu.missingPersonName] = data.missingPersonName;
      const dob = data.missingPersonDOB ?? data.missingPersonDob;
      if (dob !== undefined) p[FReu.missingPersonDOB] = dob;
      if (data.missingPersonPhone !== undefined) p[FReu.missingPersonPhone] = data.missingPersonPhone;
      if (data.matchedSurvivorId !== undefined) p[FReu.matchedSurvivorId] = data.matchedSurvivorId;
      if (data.matchConfidence !== undefined) p[FReu.matchConfidence] = data.matchConfidence;
      if (data.status !== undefined) p[FReu.status] = data.status;
      if (data.missingPersonDescription !== undefined) p[FReu.missingPersonDescription] = data.missingPersonDescription;
      if (data.notes !== undefined) p[FReu.notes] = data.notes;
      await updateRecord(TABLES.shelterReunification, id, p);
      await get().loadReunification();
    } catch (e) { console.error('[Shelter]', e); }
  },
  loadingMedical: false,
  updateMedicalEntry: async (id: any, data: any) => {
    try {
      const p: any = {};
      // Accept both entryType (pages) and incidentType (legacy)
      const entryType = data.entryType ?? data.incidentType;
      if (entryType !== undefined) p[FMed.incidentType] = entryType;
      if (data.description !== undefined) p[FMed.description] = data.description;
      if (data.treatment !== undefined) p[FMed.treatment] = data.treatment;
      if (data.severity !== undefined) p[FMed.severity] = data.severity;
      // Accept both administeredBy (pages) and treatedBy (legacy)
      const administeredBy = data.administeredBy ?? data.treatedBy;
      if (administeredBy !== undefined) p[FMed.treatedBy] = administeredBy;
      if (data.referral !== undefined) p[FMed.referral] = data.referral;
      if (data.notes !== undefined) p[FMed.notes] = data.notes;
      if (data.medicationName !== undefined) p[FMed.medicationName] = data.medicationName;
      if (data.dosage !== undefined) p[FMed.dosage] = data.dosage;
      if (data.followUpNeeded !== undefined) p[FMed.followUpNeeded] = data.followUpNeeded;
      if (data.followUpDate !== undefined) p[FMed.followUpDate] = data.followUpDate;
      await updateRecord(TABLES.shelterMedicalLog, id, p);
      await get().fetchMedicalLog();
    } catch (e) { console.error('[Shelter] updateMedicalEntry', e); }
  },
  loadingPets: false,
  loadingReferrals: false,
  updateReferral: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.survivorId !== undefined) p[FRef.survivorId] = data.survivorId;
      if (data.shelterId !== undefined) p[FRef.shelterId] = data.shelterId;
      if (data.referralType !== undefined) p[FRef.referralType] = data.referralType;
      if (data.agency !== undefined) p[FRef.agency] = data.agency;
      if (data.contactInfo !== undefined) p[FRef.contactInfo] = data.contactInfo;
      if (data.status !== undefined) p[FRef.status] = data.status;
      if (data.notes !== undefined) p[FRef.notes] = data.notes;
      await updateRecord(TABLES.shelterReferrals, id, p);
      await get().fetchReferrals();
    } catch (e) { console.error('[Shelter] updateReferral', e); }
  },
  loadingTransfers: false,
  updateTransfer: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.toShelterId !== undefined) p[FTr.toShelterId] = data.toShelterId;
      if (data.reason !== undefined) p[FTr.reason] = data.reason;
      // Accept both initiatedBy (pages) and authorizedBy (legacy)
      const initiatedBy = data.initiatedBy ?? data.authorizedBy;
      if (initiatedBy !== undefined) p[FTr.authorizedBy] = initiatedBy;
      if (data.notes !== undefined) p[FTr.notes] = data.notes;
      if (data.status !== undefined) p[FTr.status] = data.status;
      if ((data as any).completedAt !== undefined) p[FTr.completedAt] = (data as any).completedAt;
      await updateRecord(TABLES.shelterTransfers, id, p);
      await get().fetchTransfers();
    } catch (e) { console.error('[Shelter] updateTransfer', e); }
  },
  loadingVehicles: false,
}));
